
export class Optional<T> {

    public static EMPTY = new Optional();

    private constructor(private value: T | null | undefined = null) {
    }

    public map<R>(fn: (val: T) => R | null | undefined): Optional<R> {
        return this.value == null ? Optional.empty() : Optional.of(fn(this.value));
    }

    public flatMap<R>(fn: (val: T) => Optional<R>): Optional<R> {
        return this.value == null ? Optional.empty() : fn(this.value);
    }

    public get() {
        if (this.value != null) {
            return this.value;
        }
        throw Error("Cannot get from an empty Optional");
    }

    public orElse(alternative: T): T {
        return this.value == null ? alternative : this.value;
    }

    public orElseGet(fn: () => T): T {
        return this.value == null ? fn() : this.value;
    }

    public orNull(): T | null {
        return this.value == null ? null : this.value;
    }

    public orElseThrow(fn: () => Error): T {
        if (this.value == null) {
            throw fn();
        }
        return this.value;
    }

    public or(val: T | null): Optional<T> {
        return this.value == null ? Optional.of(val) : this;
    }

    public filter(fn: (val: T) => boolean): Optional<T> {
        return this.value == null ? Optional.empty() : Optional.of(fn(this.value) ? this.value : null);
    }

    public isPresent(): boolean {
        return this.value != null;
    }

    public isEmpty(): boolean {
        return !this.isPresent();
    }

    public doIfPresent(consumer: (val: T) => void): Optional<T> {
        if (this.value != null) {
            consumer(this.value)
        }
        return this
    }

    public ifPresent(consumer: (val: T) => void) {
        if (this.value != null) {
            consumer(this.value);
        }
    }

    public doIfNotPresent(fn: () => void): Optional<T> {
        if (this.value == null) {
            fn()
        }
        return this
    }

    public ifNotPresent(fn: () => void) {
        if (this.value == null) {
            fn();
        }
    }

    public static of<T>(val: T | null | undefined): Optional<T> {
        return val == null ? Optional.empty() : new Optional(val);
    }

    public static empty<T>(): Optional<T> {
        return Optional.EMPTY as Optional<T>;
    }
}
